import React from "react";
import MetricCards from "features/analytics/studio/components/charts/MetricCards";
import { formatNumber } from "shared/utils/numberFormatter";
import { formatDurationSeconds } from "features/analytics/studio/utils/durationFormat";

function segmentLabel(seg) {
  return (
    seg?.breakdownDisplay ||
    seg?.breakdownValue ||
    seg?.breakdownKey ||
    "Segment"
  );
}

function formatPrimaryForSteps(steps, measuring, segmentSteps, segment) {
  const n = steps?.length || 0;
  if (n < 1) return { value: 0, valueDisplay: "—", unit: "" };

  const lastKey = `step${n}`;
  const first = segmentSteps?.step1 ?? steps[0];
  const last = segmentSteps?.[lastKey] ?? steps[n - 1];
  if (!first || !last) return { value: 0, valueDisplay: "—", unit: "" };

  switch (measuring) {
    case "conversion_rate": {
      const pct =
        typeof segment?.overallConversion === "number"
          ? segment.overallConversion
          : typeof last.overallConversion === "number"
            ? last.overallConversion
            : first.count > 0
              ? ((last.count || 0) / first.count) * 100
              : 0;
      const rounded = Math.round(pct * 10) / 10;
      return {
        value: rounded,
        valueDisplay: `${rounded.toFixed(1)}%`,
        unit: "of users convert",
      };
    }
    case "time_to_convert": {
      const sec =
        typeof last.value === "number" && last.value > 0
          ? last.value
          : null;
      const str =
        sec != null
          ? formatDurationSeconds(sec)
          : last.timeToConvert || "—";
      return {
        value: sec ?? 0,
        valueDisplay: str,
        unit: "time to convert",
      };
    }
    case "unique_users":
    case "total_conversions":
    case "total_sessions":
    case "property_sum": {
      const raw =
        last.value !== undefined && last.value !== ""
          ? last.value
          : last.count ?? 0;
      const num = Number(raw);
      const v = Number.isFinite(num) ? num : 0;
      const unit =
        measuring === "unique_users"
          ? "users"
          : measuring === "total_sessions"
            ? "sessions"
            : measuring === "property_sum"
              ? "sum"
              : "conversions";
      return {
        value: v,
        valueDisplay: formatNumber(v),
        unit,
      };
    }
    default: {
      const c = last.count ?? 0;
      return {
        value: c,
        valueDisplay: formatNumber(c),
        unit: "users",
      };
    }
  }
}

function metricTitleForMeasuring(measuring) {
  switch (measuring) {
    case "conversion_rate":
      return "Overall conversion";
    case "time_to_convert":
      return "Time to convert";
    case "unique_users":
      return "Unique users (completed)";
    case "total_conversions":
      return "Total conversions";
    case "total_sessions":
      return "Total sessions";
    case "property_sum":
      return "Property sum";
    default:
      return "Funnel metric";
  }
}

/**
 * Insights-style metric cards for funnel reports (replaces table-style metric view).
 */
export default function FunnelMetricCards({
  steps = [],
  breakdowns = [],
  measuring = "conversion_rate",
  seriesColors,
}) {
  const hasBreakdowns = Array.isArray(breakdowns) && breakdowns.length > 0;

  if (!hasBreakdowns) {
    const lastStep = steps[steps.length - 1];
    const { value, valueDisplay, unit } = formatPrimaryForSteps(
      steps,
      measuring,
      null,
      typeof lastStep?.overallConversion === "number"
        ? { overallConversion: lastStep.overallConversion }
        : null
    );
    const series = [
      {
        metricId: "funnel-overall",
        metricIdentifier: "A",
        metricName: metricTitleForMeasuring(measuring),
        displayLabel: metricTitleForMeasuring(measuring),
        summary: { total: value },
        valueDisplay,
        unit,
      },
    ];
    return <MetricCards series={series} seriesColors={seriesColors} />;
  }

  const series = breakdowns.map((seg, index) => {
    const segmentSteps = seg.steps || {};
    const { value, valueDisplay, unit } = formatPrimaryForSteps(
      steps,
      measuring,
      segmentSteps,
      seg
    );
    const id = String.fromCharCode(65 + index);
    const label = segmentLabel(seg);
    return {
      metricId: seg.breakdownKey || `seg-${index}`,
      metricIdentifier: id,
      metricName: `${metricTitleForMeasuring(measuring)}: ${label}`,
      displayLabel: label,
      fullMetricName: metricTitleForMeasuring(measuring),
      originalBreakdownKey: label,
      summary: { total: value },
      valueDisplay,
      unit,
    };
  });

  return <MetricCards series={series} seriesColors={seriesColors} />;
}
